/**
 * useGuide — the guided-first-recipe "brain" (onboarding revival T2).
 *
 * Plan: .plan/features/2026-07-02-onboarding-guided-first-recipe.md (T2).
 *
 * The guide walks a brand-new account through four observed-completion beats.
 * This hook owns the LOGIC only — no UI. The render site (a hairline GuideStrip
 * under the RecipeHeader) lands in a later track (T4) and consumes this hook's
 * return: the ordered `beats`, the lowest-incomplete `currentBeat`, and the
 * `completeBeat` / `dismiss` setters.
 *
 * Arm / suppress (plan T2):
 *   The guide is ARMED only while the account's `onboardingCompletedAt` is null.
 *   Once completion is stamped (all four beats done, or the user dismisses) the
 *   `onComplete` callback fires the cross-device `account.completeOnboarding`
 *   RPC, and every future session/device sees `onboardingCompletedAt !== null`
 *   → the guide stays suppressed. Dismiss is a PERMANENT stamp (adopted
 *   decision, plan §"Decisions adopted").
 *
 * Per-device ledger (plan T2):
 *   Beat completion is remembered per device in localStorage under
 *   `proof.guide.v1`. This is a fast local cache so a returning user on the same
 *   device resumes mid-guide without waiting on a network read.
 *
 *   FAIL-OPEN policy (plan T2): "any storage error disables the guide silently,
 *   never breaks the app." A localStorage ACCESS error (private mode, disabled
 *   storage) → the guide is turned OFF (`active === false`) and never throws. A
 *   malformed / corrupt STORED VALUE is not an access error: it self-heals by
 *   resetting to an empty ledger (the guide continues from beat one).
 *
 * Self-healing (plan T2):
 *   The active beat is DERIVED as the lowest-incomplete beat, so a ledger where
 *   a later beat is marked done but an earlier one is not never "gets stuck" —
 *   the strip simply points at the earliest unfinished beat. Any unrecognised
 *   beat id or non-array shape is dropped on read.
 *
 * Structured funnel logs (plan T2): the guide emits `{ event, … }` console
 * records — the app's structured-log idiom (cf. `recipe_list_failed` in
 * routes/app.tsx) — for: armed, beat completed (which), dismissed, finished.
 *
 * Voice: this module ships no user-facing strings beyond the beat labels, which
 * are quiet, imperative, no exclamations, no banned words (branding.md §5 /
 * §5.2.1). The labels are the strip's default copy; T4 owns the render.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ── Storage key ─────────────────────────────────────────────────────────────
// `proof.`-prefixed browser-storage key. (The `proof-*` Cloudflare-resource
// rule governs CF resources, not localStorage keys — cf. `pip.firsts.v1`.)
export const GUIDE_STORAGE_KEY = "proof.guide.v1" as const;

// ── Beats ───────────────────────────────────────────────────────────────────

/** The four ordered guide beats. Order is load-bearing (lowest-incomplete). */
export type GuideBeatId = "compute" | "save-version" | "cook-scale" | "rate";

interface GuideBeatDef {
  readonly id: GuideBeatId;
  /** Quiet imperative label — the strip's default copy (branding.md §5). */
  readonly label: string;
}

/**
 * The canonical beat table (plan T2):
 *   1. a labelled line computes
 *   2. Save as version
 *   3. Cook + scale
 *   4. rate
 * Do not reorder — the lowest-incomplete derivation depends on this order.
 */
const GUIDE_BEAT_DEFS: readonly GuideBeatDef[] = [
  { id: "compute", label: "Write a labelled line" },
  { id: "save-version", label: "Save as version" },
  { id: "cook-scale", label: "Cook and scale" },
  { id: "rate", label: "Rate a cook" },
] as const;

const BEAT_IDS: readonly GuideBeatId[] = GUIDE_BEAT_DEFS.map((b) => b.id);
const BEAT_ID_SET: ReadonlySet<string> = new Set(BEAT_IDS);

// ── Ledger shape + normalisation ────────────────────────────────────────────

interface GuideLedger {
  readonly v: 1;
  readonly completed: readonly GuideBeatId[];
  readonly dismissed: boolean;
}

const EMPTY_LEDGER: GuideLedger = { v: 1, completed: [], dismissed: false };

/**
 * Coerce an arbitrary parsed value into a valid ledger. Any corruption —
 * non-object, non-array `completed`, unknown beat ids, wrong types — is dropped
 * (self-heal → reset to a clean shape). Never throws.
 */
function normaliseLedger(parsed: unknown): GuideLedger {
  if (typeof parsed !== "object" || parsed === null) return EMPTY_LEDGER;
  const obj = parsed as Record<string, unknown>;
  const completedRaw = obj["completed"];
  const completed = Array.isArray(completedRaw)
    ? [
        ...new Set(
          completedRaw.filter((v): v is GuideBeatId => typeof v === "string" && BEAT_ID_SET.has(v)),
        ),
      ]
    : [];
  return { v: 1, completed, dismissed: obj["dismissed"] === true };
}

// ── Fail-open storage helpers ───────────────────────────────────────────────

/** Resolve localStorage, guarding SSR + access throws. `undefined` → storage off. */
function safeLocalStorage(): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    // Property access itself can throw in locked-down browsers → storage off.
    return undefined;
  }
}

/**
 * Read the ledger.
 *   - `null`  → storage is UNAVAILABLE (access threw / SSR) → disable the guide.
 *   - ledger  → a valid (possibly self-healed) ledger; the guide may run.
 * A missing key returns the empty ledger (fresh user). Malformed JSON returns
 * the empty ledger (self-heal). Never throws.
 */
function readLedger(storage: Storage | undefined): GuideLedger | null {
  if (!storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(GUIDE_STORAGE_KEY);
  } catch {
    // getItem threw → treat storage as unavailable (disable the guide).
    return null;
  }
  if (raw == null) return EMPTY_LEDGER;
  try {
    return normaliseLedger(JSON.parse(raw) as unknown);
  } catch {
    // Corrupt JSON → self-heal to an empty ledger (the guide continues).
    return EMPTY_LEDGER;
  }
}

/** Persist the ledger. Swallows write failures (quota / disabled) — never throws. */
function writeLedger(storage: Storage | undefined, ledger: GuideLedger): void {
  if (!storage) return;
  try {
    storage.setItem(GUIDE_STORAGE_KEY, JSON.stringify(ledger));
    // eslint-disable-next-line no-restricted-syntax -- best-effort persist: on quota / disabled storage the beat may be re-recorded next session, which is harmless (Rule 8 documented degradation).
  } catch {
    // Write failed — the in-memory ledger still drives the UI this session.
  }
}

// ── Structured funnel logs ──────────────────────────────────────────────────

type GuideFunnelEvent =
  | { event: "guide_armed" }
  | { event: "guide_beat_completed"; beat: GuideBeatId }
  | { event: "guide_dismissed" }
  | { event: "guide_finished" };

/** Emit a structured funnel record ({ event, … }) — the app's console idiom. */
function logGuide(payload: GuideFunnelEvent): void {
  // eslint-disable-next-line no-console -- structured funnel log (cf. recipe_list_failed in routes/app.tsx).
  console.info(payload);
}

// ── Public API ──────────────────────────────────────────────────────────────

export interface UseGuideOptions {
  /**
   * The account's onboarding-completion timestamp, or `null` when the account
   * has not yet completed onboarding. The guide is armed only while this is
   * `null` (plan T2 arm/suppress).
   */
  onboardingCompletedAt: number | null;
  /**
   * Fired exactly once when the guide reaches a terminal state — all four beats
   * complete, or the user dismisses. Wire this to the cross-device
   * `account.completeOnboarding` RPC so suppression persists across devices.
   * Failures should be handled by the caller (non-fatal — the per-device ledger
   * still suppresses locally). May be sync or async.
   */
  onComplete?: () => void | Promise<unknown>;
}

/** One beat, with its resolved completion state. */
export interface GuideBeat {
  readonly id: GuideBeatId;
  readonly label: string;
  readonly done: boolean;
}

export interface GuideState {
  /** True while the account is eligible for the guide (onboardingCompletedAt === null). */
  readonly armed: boolean;
  /**
   * True when the strip should render: armed, storage is available, not
   * dismissed, and not all beats complete. T4 gates its render on this.
   */
  readonly active: boolean;
  /** The four beats in order, each with `done`. */
  readonly beats: readonly GuideBeat[];
  /** The lowest-incomplete beat (self-healing), or `null` when all are done. */
  readonly currentBeat: GuideBeat | null;
  /** Mark a beat complete. Idempotent; no-op when suppressed / storage-off. */
  readonly completeBeat: (id: GuideBeatId) => void;
  /** Dismiss the guide permanently (stamps completion). No-op when suppressed. */
  readonly dismiss: () => void;
}

/**
 * The guide brain. Safe to call unconditionally: when the account is already
 * onboarded (or storage is unavailable) it simply returns `active: false` and
 * the setters become no-ops.
 */
export function useGuide({ onboardingCompletedAt, onComplete }: UseGuideOptions): GuideState {
  const armed = onboardingCompletedAt === null;

  // Lazy-init the ledger from storage. `null` = storage unavailable → guide off.
  const [ledger, setLedger] = useState<GuideLedger | null>(() => readLedger(safeLocalStorage()));

  const storageOk = ledger !== null;

  // ── Derived state (self-healing) ──────────────────────────────────────────
  const completedSet = useMemo<ReadonlySet<GuideBeatId>>(
    () => new Set(ledger?.completed ?? []),
    [ledger],
  );

  const beats = useMemo<readonly GuideBeat[]>(
    () => GUIDE_BEAT_DEFS.map((b) => ({ id: b.id, label: b.label, done: completedSet.has(b.id) })),
    [completedSet],
  );

  // Lowest-incomplete beat — order-driven, so a corrupt "later done, earlier
  // not" ledger points at the earliest unfinished beat rather than getting stuck.
  const currentBeat = useMemo<GuideBeat | null>(() => beats.find((b) => !b.done) ?? null, [beats]);

  const allComplete = currentBeat === null;
  const dismissed = ledger?.dismissed ?? false;
  const active = armed && storageOk && !dismissed && !allComplete;

  // ── Funnel: armed (once per mount) ────────────────────────────────────────
  const armedLoggedRef = useRef(false);
  useEffect(() => {
    if (armed && storageOk && !armedLoggedRef.current) {
      armedLoggedRef.current = true;
      logGuide({ event: "guide_armed" });
    }
  }, [armed, storageOk]);

  // ── Reconcile ledger → persist + transition logs + completion stamp ───────
  // Kept out of the state updaters (which React StrictMode double-invokes in
  // dev) so every side effect fires at most once per real transition.
  const completionFiredRef = useRef(false);
  const firstReconcileRef = useRef(true);
  const prevSnapshotRef = useRef<{
    completed: ReadonlySet<GuideBeatId>;
    dismissed: boolean;
  } | null>(null);

  useEffect(() => {
    if (ledger === null) return; // storage off — nothing to reconcile.

    // Persist on every change (also overwrites any corrupt stored value → heal).
    writeLedger(safeLocalStorage(), ledger);

    const cur = new Set(ledger.completed);
    const curAllComplete = BEAT_IDS.every((id) => cur.has(id));
    const terminal = ledger.dismissed || curAllComplete;

    if (armed) {
      if (firstReconcileRef.current) {
        // Mount: no transition logs (nothing "just happened"). If the account
        // already satisfies completion but the cross-device stamp is missing
        // (armed === not yet stamped), retry the stamp so it converges.
        if (terminal && !completionFiredRef.current) {
          completionFiredRef.current = true;
          void onComplete?.();
        }
      } else {
        const prev = prevSnapshotRef.current;
        for (const id of BEAT_IDS) {
          if (cur.has(id) && !(prev?.completed.has(id) ?? false)) {
            logGuide({ event: "guide_beat_completed", beat: id });
          }
        }
        if (ledger.dismissed && !(prev?.dismissed ?? false)) {
          logGuide({ event: "guide_dismissed" });
        }
        if (terminal && !completionFiredRef.current) {
          completionFiredRef.current = true;
          if (curAllComplete) logGuide({ event: "guide_finished" });
          void onComplete?.();
        }
      }
    }

    prevSnapshotRef.current = { completed: cur, dismissed: ledger.dismissed };
    firstReconcileRef.current = false;
  }, [ledger, armed, onComplete]);

  // ── Setters ───────────────────────────────────────────────────────────────
  const completeBeat = useCallback(
    (id: GuideBeatId) => {
      if (!armed) return; // suppressed accounts never record beats.
      setLedger((prev) => {
        if (prev === null) return prev; // storage off — no-op.
        if (prev.completed.includes(id)) return prev; // idempotent.
        return { ...prev, completed: [...prev.completed, id] };
      });
    },
    [armed],
  );

  const dismiss = useCallback(() => {
    if (!armed) return;
    setLedger((prev) => {
      if (prev === null) return prev;
      if (prev.dismissed) return prev;
      return { ...prev, dismissed: true };
    });
  }, [armed]);

  // Memoise the returned state object. Without this the hook hands back a fresh
  // object literal every render; when it drives a React context (GuideProvider,
  // T2), that new reference churns EVERY consumer on each parent render — the
  // review flag T4 folds in. `beats` / `currentBeat` are already memoised and
  // `completeBeat` / `dismiss` are stable useCallbacks, so this memo only
  // produces a new object when a field genuinely changes.
  return useMemo<GuideState>(
    () => ({ armed, active, beats, currentBeat, completeBeat, dismiss }),
    [armed, active, beats, currentBeat, completeBeat, dismiss],
  );
}
